export type VideoSource =
    | { provider: 'vimeo', id: string, hash: string | null }
    | { provider: 'youtube', id: string }

const VIMEO_PATTERN = /vimeo\.com\/(?:[^?#]*\/)?(\d{6,})(?:\/([0-9a-f]{6,}))?/i
const VIMEO_HASH_PATTERN = /[?&]h=([0-9a-f]{6,})/i
const YOUTUBE_PATTERN = /(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:[^#]*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/

export const parseVideoUrl = (url: string | null | undefined): VideoSource | null => {
    if (!url) return null

    const value = url.trim()
    const vimeo = value.match(VIMEO_PATTERN)

    if (vimeo) {
        const hash = vimeo[2] ?? value.match(VIMEO_HASH_PATTERN)?.[1] ?? null

        return { provider: 'vimeo', id: vimeo[1], hash }
    }

    const youtube = value.match(YOUTUBE_PATTERN)

    if (youtube) return { provider: 'youtube', id: youtube[1] }

    return null
}

const vimeoBase = (source: { id: string, hash: string | null }): string =>
    `https://player.vimeo.com/video/${source.id}${source.hash ? `?h=${source.hash}&` : '?'}`

export const getPlayerUrl = (source: VideoSource): string => {
    if (source.provider === 'vimeo') return `${vimeoBase(source)}autoplay=1&title=0&byline=0&portrait=0&dnt=1&color=f1e7d2`

    return `https://www.youtube-nocookie.com/embed/${source.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`
}

export const getBackgroundUrl = (source: VideoSource): string | null => {
    if (source.provider !== 'vimeo') return null

    return `${vimeoBase(source)}background=1&dnt=1&quality=auto`
}

export const getWatchUrl = (source: VideoSource): string => {
    if (source.provider === 'vimeo') return `https://vimeo.com/${source.id}${source.hash ? `/${source.hash}` : ''}`

    return `https://www.youtube.com/watch?v=${source.id}`
}

export const getProviderName = (source: VideoSource): string => source.provider === 'vimeo' ? 'Vimeo' : 'YouTube'

export const parseAspect = (value: string | null | undefined, fallback = 16 / 9): number => {
    const match = value?.match(/^\s*(\d+(?:\.\d+)?)\s*[:/x]\s*(\d+(?:\.\d+)?)\s*$/i)

    if (!match) return fallback

    const ratio = Number(match[1]) / Number(match[2])

    return Number.isFinite(ratio) && ratio > 0 ? ratio : fallback
}
